import React, { useState, useEffect, useRef } from "react"
import {
  Checkbox,
  CircularProgress,
  FormHelperText,
  TextField,
  Select,
  MenuItem,
  ListItemText,
  Button,
  OutlinedInput,
  FormControl,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import ClearIcon from "@material-ui/icons/Clear"
import { SearchIcon } from "@iconsGallery"
import { useTranslation } from "react-i18next"
import * as QA_Automation from "@qaAutomation"

const useStyles = makeStyles({
  formControl: {
    "& .MuiFormLabel-root.Mui-focused": {
      color: "var(--primaryColor)",
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: "var(--primaryColor)",
      },
    },
  },
  menuCheckbox: {
    minHeight: "28px !important",
    height: "auto",
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
    whiteSpace: "normal",
    wordBreak: "break-word",
    "& .MuiCheckbox-root": {
      padding: "4px !important",
    },
    "& .MuiListItemText-root": {
      marginTop: "0 !important",
      marginBottom: "0 !important",
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    },
  },
  textInput: {
    width: "100%",
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--primaryColor)",
    },
  },
  scrollContainer: {
    minHeight: "120px",
    maxHeight: "240px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "var(--primaryColor)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "var(--primaryColor)",
      filter: "brightness(0.9)",
    },
    scrollbarWidth: "thin",
  },
})

// Utility: check for empty value
const isEmptyValue = (val) => {
  if (val === undefined || val === null || val === "") return true
  if (Array.isArray(val) && val.length === 0) return true
  if (typeof val === "object" && Object.keys(val).length === 0) return true
  return false
}

// Utility: extract display name from object or string
const getItemDisplayName = (item, labelKey) => {
  if (item === null || item === undefined) return ""
  if (typeof item !== "object") return String(item)
  if (labelKey && item[labelKey] !== undefined) return String(item[labelKey])
  if (item.name !== undefined) return String(item.name)
  if (item.locationName !== undefined) return String(item.locationName)
  if (item.label !== undefined) return String(item.label)
  if (item.categoryName !== undefined) return String(item.categoryName)
  if (item.category_name !== undefined) return String(item.category_name)
  if (item.storeName !== undefined) return String(item.storeName)
  if (item.store_name !== undefined) return String(item.store_name)
  if (item.id !== undefined) return String(item.id)
  if (item._id !== undefined) return String(item._id)
  return String(item)
}

// Utility: extract value identifier from object or string
const getItemVal = (item, valueKey, isPlain) => {
  if (isPlain) return item
  if (item === null || item === undefined) return item
  if (typeof item !== "object") return item
  if (valueKey && item[valueKey] !== undefined) return item[valueKey]
  if (item.id !== undefined) return item.id
  if (item._id !== undefined) return item._id
  if (item.locationId !== undefined) return item.locationId
  if (item.value !== undefined) return item.value
  if (item.name !== undefined) return item.name
  if (item.locationName !== undefined) return item.locationName
  return item
}

// In-memory cache for mapping selected value IDs to their display names
const itemNameCache = {}
const cacheDropdownItemNames = (items, valueKey, labelKey) => {
  if (!items || !Array.isArray(items)) return
  items.forEach((item) => {
    if (item !== null && item !== undefined) {
      const name = getItemDisplayName(item, labelKey)
      const val = getItemVal(item, valueKey, false)
      if (val !== undefined && val !== null && name) {
        itemNameCache[String(val)] = name
      }
      if (typeof item === "object") {
        if (item.id !== undefined && name) itemNameCache[String(item.id)] = name
        if (item._id !== undefined && name) itemNameCache[String(item._id)] = name
        if (item.locationId !== undefined && name) itemNameCache[String(item.locationId)] = name
      }
    }
  })
}

const getDisplayNameForSelectedValue = (val, items, valueKey, labelKey) => {
  if (val === null || val === undefined || val === "") return ""
  if (typeof val === "object") return getItemDisplayName(val, labelKey)
  if (itemNameCache[String(val)]) return itemNameCache[String(val)]

  if (items && Array.isArray(items)) {
    const found = items.find((item) => {
      const itemV = getItemVal(item, valueKey, false)
      return (
        String(itemV) === String(val) ||
        (typeof item === "object" &&
          (String(item?.id) === String(val) ||
            String(item?._id) === String(val) ||
            String(item?.locationId) === String(val) ||
            String(item?.name) === String(val)))
      )
    })
    if (found) {
      const name = getItemDisplayName(found, labelKey)
      itemNameCache[String(val)] = name
      return name
    }
  }
  return String(val)
}

// Utility: check whether item is selected in selectedList
const isItemSelected = (item, selectedList, valueKey, isPlain) => {
  if (isEmptyValue(selectedList)) return false
  if (item === null || item === undefined) return false

  const list = Array.isArray(selectedList) ? selectedList : [selectedList]

  return list.some((selected) => {
    if (selected === null || selected === undefined || selected === "") return false
    if (selected === item) return true

    const selVal = typeof selected === "object" ? getItemVal(selected, valueKey, false) : selected
    const itemVal = typeof item === "object" ? getItemVal(item, valueKey, false) : item

    if (selVal !== undefined && itemVal !== undefined && selVal !== null && itemVal !== null) {
      if (selVal === itemVal || String(selVal) === String(itemVal)) return true
    }

    if (typeof selected === "object" && typeof item === "object") {
      if (selected._id !== undefined && item._id !== undefined && String(selected._id) === String(item._id)) return true
      if (selected.id !== undefined && item.id !== undefined && String(selected.id) === String(item.id)) return true
      if (selected.locationId !== undefined && item.locationId !== undefined && String(selected.locationId) === String(item.locationId)) return true
      if (valueKey && selected[valueKey] !== undefined && item[valueKey] !== undefined && String(selected[valueKey]) === String(item[valueKey])) return true
      if (selected.name !== undefined && item.name !== undefined && String(selected.name) === String(item.name)) return true
    }

    return false
  })
}

/**
 * SearchMultiSelectDropdown
 * 
 * Dynamic Multi-Select Dropdown with Checkbox, Search, and Show More.
 * - Supports both Objects and Strings seamlessly.
 * - Independent "Select All": selecting all search results will NOT activate Select All.
 * - "Select All" is dedicated for totalDocs and avoids sending filtered payload.
 * - Clicking "Show More" when Select All is active loads new records with selected checkboxes.
 */
function SearchMultiSelectDropdown(props) {
  const {
    id = "id-multi-select-search-dropdown",
    label = "SELECT",
    placeholder = "SEARCH",
    data = [],
    filteredItems = [],
    searchTerm = "",
    handleSearchTerm = () => {},
    handleDropdownChange = () => {},
    handleShowMore = () => {},
    totalDocs = 0,
    valueIdentifier = "id",
    labelIdentifier = "name",
    isPlainObj = false,
    selectAll = true,
    checkbox = true,
    showDropdownItems = 2,
    fullWidth = true,
    error = false,
    helperText = "",
    disabled = false,
    disableMenuName = "",
    isShowFooter = true,
    isShowSearch = true,
    searchBox,
    selectionLimit = 0,
  } = props || {}

  const showSearch = searchBox !== undefined ? Boolean(searchBox) : isShowSearch
  const classes = useStyles()
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState(searchTerm || "")
  const [selectedItems, setSelectedItems] = useState(
    isEmptyValue(filteredItems) ? [] : filteredItems
  )

  const selectRef = useRef(null)
  const isExplicitSelectAllRef = useRef(false)
  const searchTimeoutRef = useRef(null)
  const prevDataRef = useRef([])
  const selectedItemsRef = useRef(selectedItems)
  const handleDropdownChangeRef = useRef(handleDropdownChange)
  // Prevents the filteredItems sync from wiping local selections
  // while a search API call is in flight (parent may briefly reset filteredItems to []).
  const isSearchingRef = useRef(false)

  useEffect(() => {
    selectedItemsRef.current = selectedItems
  })

  useEffect(() => {
    handleDropdownChangeRef.current = handleDropdownChange
  })

  // Sync external searchTerm prop
  useEffect(() => {
    setSearchText(searchTerm || "")
  }, [searchTerm])

  // Sync external filteredItems prop.
  // Skipped while a search is in flight to prevent the parent's temporary
  // filteredItems reset from visually unchecking locally-selected checkboxes.
  useEffect(() => {
    if (isSearchingRef.current) return
    if (isEmptyValue(filteredItems)) {
      setSelectedItems([])
      isExplicitSelectAllRef.current = false
    } else {
      setSelectedItems(filteredItems ?? [])
    }
  }, [filteredItems])

  // Cache display names and handle auto-selection on pagination ("Show More") when Select All is active.
  // Also auto-selects search results when Select All is active.
  useEffect(() => {
    cacheDropdownItemNames(data, valueIdentifier, labelIdentifier)

    if (selectAll && data && data.length > 0) {
      const currentSelected = selectedItemsRef.current || []
      const wasSelectAll = isExplicitSelectAllRef.current

      const isPaginationAppend =
        !searchText &&
        !searchTerm &&
        data.length > prevDataRef.current.length

      const shouldAutoSelect = wasSelectAll && (isPaginationAppend || !!(searchText || searchTerm))

      if (shouldAutoSelect) {
        const toAdd = isPlainObj
          ? data.filter((item) => !isItemSelected(item, currentSelected, valueIdentifier, true))
          : data
              .map((item) => getItemVal(item, valueIdentifier, false))
              .filter((val) => !isItemSelected(val, currentSelected, valueIdentifier, false))

        if (toAdd.length > 0) {
          const newSelected = [
            ...(Array.isArray(currentSelected) ? currentSelected : [currentSelected]),
            ...toAdd,
          ]
          setSelectedItems(newSelected)
          handleDropdownChangeRef.current?.(
            newSelected,
            label,
            isPlainObj ? "isPlainObj" : "",
            "select-all"
          )
        }
      }
    }

    prevDataRef.current = data ? [...data] : []
  }, [data, selectAll, isPlainObj, valueIdentifier, labelIdentifier, label, searchText, searchTerm])

  // Clear loading spinner when data arrives and unblock the filteredItems sync
  useEffect(() => {
    setLoading(false)
    isSearchingRef.current = false
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
  }, [data])

  // Close dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideSelect = selectRef.current && selectRef.current.contains(event.target)
      const isInsideMenu =
        event.target &&
        (event.target.closest?.(".MuiMenu-paper") ||
          event.target.closest?.(".MuiPopover-root"))
      if (!isInsideSelect && !isInsideMenu) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // QA Automation hook
  useEffect(() => {
    QA_Automation?.doAddOnTestIdCommonAutoCompleteDropdown?.(selectRef, props)
  }, [selectedItems, searchTerm, filteredItems])

  // Search handler
  const handleSearch = (val) => {
    isSearchingRef.current = true
    setLoading(true)
    setSearchText(val)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setLoading(false)
      isSearchingRef.current = false
    }, 3000)
    handleSearchTerm(val, label)
  }

  // Clear Search
  const handleClearSearch = (e) => {
    if (e) {
      if (typeof e.stopPropagation === "function") e.stopPropagation()
      if (typeof e.preventDefault === "function") e.preventDefault()
    }
    setSearchText("")
    handleSearch("")
  }

  // Handle Select All click: dedicated to selecting all totalDocs
  const handleSelectAllToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const isCurrentlyAll = Boolean(isExplicitSelectAllRef.current)
    let newSelected

    if (isCurrentlyAll) {
      // Deselect all
      newSelected = []
      isExplicitSelectAllRef.current = false
    } else {
      // Select all loaded items
      const toAdd = isPlainObj
        ? data.filter((item) => !isItemSelected(item, selectedItems, valueIdentifier, true))
        : data
            .map((item) => getItemVal(item, valueIdentifier, false))
            .filter((val) => !isItemSelected(val, selectedItems, valueIdentifier, false))
      newSelected = [...(selectedItems || []), ...toAdd]
      isExplicitSelectAllRef.current = true
    }

    setSelectedItems(newSelected)
    handleDropdownChange(
      newSelected,
      label,
      isPlainObj ? "isPlainObj" : "",
      isCurrentlyAll ? "deselect-all" : "select-all"
    )
  }

  // Handle individual item click
  const handleItemToggle = (item, e) => {
    e.preventDefault()
    e.stopPropagation()

    const exists = isItemSelected(item, selectedItems, valueIdentifier, isPlainObj)
    let newSelected

    if (exists) {
      newSelected = (selectedItems || []).filter(
        (i) => !isItemSelected(item, [i], valueIdentifier, isPlainObj)
      )
    } else {
      const valToAdd = isPlainObj ? item : getItemVal(item, valueIdentifier, false)
      newSelected = [...(selectedItems || []), valToAdd]
    }

    if (selectionLimit > 0 && newSelected.length > selectionLimit) return

    // Selecting individual items strictly resets Select All mode
    isExplicitSelectAllRef.current = false
    setSelectedItems(newSelected)
    handleDropdownChange(newSelected, label, isPlainObj ? "isPlainObj" : "", "")
  }

  // Select All is active ONLY if explicitly clicked
  const isAllSelected = Boolean(isExplicitSelectAllRef.current)

  return (
    <FormControl className={classes.formControl} fullWidth={fullWidth}>
      <Select
        ref={selectRef}
        id={id}
        multiple
        disabled={disabled}
        value={selectedItems ?? []}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        displayEmpty
        input={
          <OutlinedInput
            margin="dense"
            style={{ width: "30vh", height: "36px" }}
          />
        }
        renderValue={(selected) => {
          // If Select All is active, display "ALL"
          if (selectAll && isAllSelected) {
            return <b>{t("ALL")}</b>
          }

          // Render selected items names
          if (selected && selected.length > 0) {
            const selectedArray = Array.isArray(selected) ? selected : [selected]
            const selectedNames = selectedArray.map((v) =>
              isPlainObj
                ? getItemDisplayName(v, labelIdentifier)
                : getDisplayNameForSelectedValue(v, data, valueIdentifier, labelIdentifier)
            )

            const remaining = selectedNames.length - showDropdownItems
            return (
              <b>
                {selectedNames.slice(0, showDropdownItems).join(", ")}
                {selectedNames.length > showDropdownItems &&
                  ` ${t("AND")} ${remaining} ${t("MORE")}`}
              </b>
            )
          }

          return <b>{t(label)}</b>
        }}
        style={{
          backgroundColor: "transparent",
          height: "38px",
          width: "100%",
        }}
        MenuProps={{
          style: { zIndex: 1300 },
          PaperProps: {
            style: {
              maxHeight: "none",
              width: 260,
              zIndex: 1300,
            },
          },
          getContentAnchorEl: () => null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
        }}
      >
        {/* Search Bar */}
        {showSearch && (
          <MenuItem
            key="search-input-item"
            value=""
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onMouseDown={(e) => {
              e.stopPropagation()
            }}
            style={{
              padding: "5px 10px 10px 10px",
              backgroundColor: "#ffffff",
              borderBottom: "1px solid #e0e0e0",
              cursor: "default",
            }}
          >
            <TextField
              autoFocus
              size="small"
              placeholder={t(placeholder || "SEARCH")}
              variant="outlined"
              value={searchText}
              onClick={(e) => {
                e.stopPropagation()
              }}
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
              onKeyDown={(e) => {
                e.stopPropagation()
              }}
              InputProps={{
                endAdornment: (
                  <React.Fragment>
                    {searchText ? (
                      <ClearIcon
                        style={{ color: "#888", cursor: "pointer", fontSize: "18px" }}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleClearSearch(e)
                        }}
                      />
                    ) : (
                      <SearchIcon style={{ color: "#CCC" }} />
                    )}
                  </React.Fragment>
                ),
              }}
              className={classes.textInput}
              onChange={(event) => {
                handleSearch(event.target.value.replace(/(\s{2,})/g, " "))
              }}
            />
          </MenuItem>
        )}

        {/* Select All Option - hidden during search so search results are selected individually */}
        {selectAll && selectionLimit !== 1 && !searchText && !searchTerm && data && data.length > 0 && (
          <MenuItem
            key="select-all-menu-item"
            value="select-all-option"
            onClick={handleSelectAllToggle}
            style={{
              minHeight: "28px",
              height: "auto",
              paddingTop: "2px",
              paddingBottom: "2px",
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#ffffff",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            <Checkbox
              checked={isAllSelected}
              style={{
                padding: "4px",
                marginRight: "10px",
                flexShrink: 0,
                color: "var(--primaryColor)",
                pointerEvents: "none",
              }}
            />
            <ListItemText
              primary={t("SELECT_ALL") + (totalDocs ? ` (${totalDocs})` : "")}
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                whiteSpace: "normal",
                wordBreak: "break-word",
                marginTop: 0,
                marginBottom: 0,
              }}
            />
          </MenuItem>
        )}

        {/* List of items */}
        <div className={classes.scrollContainer}>
          {loading && (!data || data.length === 0) ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "120px",
              }}
            >
              <CircularProgress size={30} style={{ color: "var(--primaryColor)" }} />
            </div>
          ) : data !== undefined && data?.length > 0 ? (
            <>
              {data.map((item, idx) => {
                const isChecked = isItemSelected(
                  item,
                  selectedItems,
                  valueIdentifier,
                  isPlainObj
                )
                const displayName = getItemDisplayName(item, labelIdentifier)
                const isDisabled =
                  displayName === disableMenuName || item?.name === disableMenuName

                return (
                  <MenuItem
                    key={
                      item?._id ||
                      item?.id ||
                      (typeof item === "object" && item[valueIdentifier] !== undefined
                        ? item[valueIdentifier]
                        : idx)
                    }
                    value={isPlainObj ? item : getItemVal(item, valueIdentifier, false)}
                    onClick={(e) => handleItemToggle(item, e)}
                    style={{
                      minHeight: "28px",
                      height: "auto",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                    disabled={isDisabled}
                    className={classes.menuCheckbox}
                  >
                    {checkbox && (
                      <Checkbox
                        {...QA_Automation?.doAddOnTestIdCommonAutoCompleteDropdownItemCheckBox?.(
                          displayName
                        )}
                        checked={isChecked}
                        disabled={isDisabled}
                        style={{
                          padding: "4px",
                          marginRight: "10px",
                          flexShrink: 0,
                          color: "var(--primaryColor)",
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    <ListItemText
                      primary={displayName}
                      style={{
                        fontSize: "14px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        marginTop: 0,
                        marginBottom: 0,
                      }}
                    />
                  </MenuItem>
                )
              })}
              {loading && (
                <div style={{ display: "flex", justifyContent: "center", padding: "10px" }}>
                  <CircularProgress size={24} style={{ color: "var(--primaryColor)" }} />
                </div>
              )}
            </>
          ) : (
            <MenuItem
              disabled
              style={{ justifyContent: "center", height: "120px", alignItems: "center" }}
            >
              {t("NO_ITEMS_FOUND")}
            </MenuItem>
          )}
        </div>

        {/* Show More Footer */}
        {isShowFooter && (
          <MenuItem
            disabled
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              height: "38px",
              padding: "0 16px",
              opacity: 1,
              borderTop: "1px solid rgb(224, 224, 224)",
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {totalDocs > data?.length ? (
              <Button
                type="button"
                color="primary"
                disabled={loading}
                style={{
                  fontWeight: 700,
                  pointerEvents: "auto",
                  minWidth: "auto",
                  padding: "4px 8px",
                  color: "var(--primaryColor)",
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setLoading(true)
                  handleShowMore(e, label)
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current)
                  }
                  searchTimeoutRef.current = setTimeout(() => {
                    setLoading(false)
                  }, 3000)
                }}
              >
                {t("SHOW_MORE")}
              </Button>
            ) : (
              <div />
            )}
            <span style={{ color: "rgba(0, 0, 0, 0.87)", fontSize: "14px", fontWeight: "bold" }}>
              {(data?.length || 0) +
                ` ${t("OF")} ` +
                (totalDocs !== "" && totalDocs !== null && totalDocs !== undefined
                  ? totalDocs
                  : data?.length || 0)}
            </span>
          </MenuItem>
        )}
      </Select>

      {error && (
        <FormHelperText
          error
          style={{ marginLeft: 14, marginRight: 14, marginTop: 4 }}
        >
          {t(helperText)}
        </FormHelperText>
      )}
    </FormControl>
  )
}

export { SearchMultiSelectDropdown }
export default SearchMultiSelectDropdown
